/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { accountDisplayName } from "../auth/accountLogin";
import { localUsername } from "../auth/admin/localUsername";


import FullScreenMinimiseIcon from "../icons/FullScreenMinimise.svg?react";
import { useScreenShareOrientationRooms } from "./useScreenShareVideoStyle";
import { FullscreenCallControls } from "./FullscreenCallControls";
import { useAdaptiveVideoQuality } from "./useAdaptiveVideoQuality";
import {
  useCallFullscreen,
  exitCallFullscreen,
  toggleCallFullscreen,
} from "./useCallFullscreen";
import { localizedRequestError } from "../utils/localizedError";

import {
  visibleScreenShare,
  pictureInPictureSize,
} from "./pictureInPictureSource";
import { requestPictureInPicture } from "./requestPictureInPicture";
import { renderPictureInPictureFrame } from "./renderPictureInPictureFrame";
import { ChatWidget } from "../custom-features/EphemeralChat/ChatWidget";
import { RoomContext } from "@livekit/components-react";
import { RoomEvent, type Participant } from "livekit-client";

import { type MatrixClient, type Room as MatrixRoom } from "matrix-js-sdk";
import {
  type ComponentProps,
  type FC,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from "react";
import useMeasure from "react-use-measure";
import { type MatrixRTCSession } from "matrix-js-sdk/lib/matrixrtc";
import classNames from "classnames";
import { map } from "rxjs";
import { useObservable } from "observable-hooks";
import { logger as rootLogger } from "matrix-js-sdk/lib/logger";
import { useTranslation } from "react-i18next";

import { Header, LeftNav, RightNav, RoomHeaderInfo } from "../Header";
import { HeaderStyle, useUrlParams } from "../UrlParams";
import { useCallViewKeyboardShortcuts } from "../useCallViewKeyboardShortcuts";
import { widget } from "../widget";
import styles from "./InCallView.module.css";
import { GridTile } from "../tile/GridTile";
import { SettingsModal, defaultSettingsTab } from "../settings/SettingsModal";
import { useRageshakeRequestModal } from "../settings/submit-rageshake";
import { RageshakeRequestModal } from "./RageshakeRequestModal";
import { useWakeLock } from "../useWakeLock";
import { useMergedRefs } from "../useMergedRefs";
import { type MuteStates } from "../state/MuteStates";
import { type MatrixInfo } from "./VideoPreview";
import { InviteButton } from "../button/InviteButton";
import {
  type CallViewModel,
  createCallViewModel$,
} from "../state/CallViewModel/CallViewModel.ts";
import { Grid, type TileProps } from "../grid/Grid";
import { SpotlightTile } from "../tile/SpotlightTile";
import { type EncryptionSystem } from "../e2ee/sharedKeyManagement";
import { makeGridLayout } from "../grid/GridLayout";
import { type CallLayoutOutputs } from "../grid/CallLayout";
import { makeOneOnOneLandscapeLayout } from "../grid/OneOnOneLandscapeLayout";
import { makeOneOnOnePortraitLayout } from "../grid/OneOnOnePortraitLayout";
import { makeSpotlightExpandedLayout } from "../grid/SpotlightExpandedLayout";
import { makeSpotlightLandscapeLayout } from "../grid/SpotlightLandscapeLayout";
import { makeSpotlightPortraitLayout } from "../grid/SpotlightPortraitLayout";
import { GridTileViewModel, type TileViewModel } from "../state/TileViewModel";
import {
  ReactionsSenderProvider,
  useReactionsSender,
} from "../reactions/useReactionsSender";
import { ReactionsAudioRenderer } from "./ReactionAudioRenderer";
import { ReactionsOverlay } from "./ReactionsOverlay";
import { CallEventAudioRenderer } from "./CallEventAudioRenderer";
import { matrixRTCMode as matrixRTCModeSetting } from "../settings/settings";
import { ReactionsReader } from "../reactions/ReactionsReader";
import { LivekitRoomAudioRenderer } from "../livekit/MatrixAudioRenderer.tsx";
import { muteAllAudio$ } from "../state/MuteAllAudioModel.ts";
import { useMediaDevices } from "../MediaDevicesContext.ts";
import { EarpieceOverlay } from "./EarpieceOverlay.tsx";
import {
  useAppBarHidden,
  useAppBarSecondaryButton,
  useAppBarSubtitle,
} from "../AppBar.tsx";
import { useBehavior } from "../useBehavior.ts";
import { constant } from "../state/Behavior.ts";
import { Toast } from "../Toast.tsx";
import overlayStyles from "../Overlay.module.css";
import { useTrackProcessorObservable$ } from "../livekit/TrackProcessorContext.tsx";
import { type Layout } from "../state/layout-types.ts";
import { ObservableScope } from "../state/ObservableScope.ts";
import { CallFooter, type FooterSnapshot } from "../components/CallFooter.tsx";
import { SettingsIconButton } from "../button/Button.tsx";
import { createCallFooterViewModel } from "../components/CallFooterViewModel.tsx";
import { type ViewModel } from "../state/ViewModel.ts";
import { RingingStatus } from "../tile/RingingStatus.tsx";
import { RingingAudioRenderer } from "./RingingAudioRenderer.tsx";

import { backendFetch } from "../utils/backend";
const AI_ENABLED = import.meta.env.VITE_ENABLE_AI === "true";

interface ExtendedVideoElement extends HTMLVideoElement {
  autoPictureInPicture?: boolean;
  webkitSupportsPresentationMode?: (mode: string) => boolean;
  webkitSetPresentationMode?: (mode: string) => void;
  webkitPresentationMode?: string;
}

type ExtendedMediaSession = MediaSession;

declare module "react" {
  interface CSSProperties {
    "--call-view-safe-area-inset-top"?: string;
    "--call-view-safe-area-inset-bottom"?: string;
  }
}

// ФИЛЬТР: Отсекаем ИИ-агентов и системных ботов
const isHumanParticipant = (p: Participant | undefined): boolean => {
  if (!p) return false;
  const anyP = p as unknown as {
    isAgent?: boolean;
    kind?: number | string;
    permissions?: { agent?: boolean; recorder?: boolean; hidden?: boolean };
  };

  if (anyP.isAgent) return false;
  if (
    anyP.kind === "agent" ||
    anyP.kind === "egress" ||
    anyP.kind === 4 ||
    anyP.kind === 2
  ) {
    return false;
  }
  if (
    anyP.permissions?.agent ||
    anyP.permissions?.recorder ||
    anyP.permissions?.hidden
  ) {
    return false;
  }

  const id = (p.identity || "").toLowerCase();
  if (
    id.startsWith("agent-") ||
    id.startsWith("eg_") ||
    id.startsWith("egress")
  ) {
    return false;
  }

  return id.includes(":");
};

export interface ActiveCallProps extends Omit<
  InCallViewProps,
  "vm" | "livekitRoom" | "connState" | "footerVm"
> {
  e2eeSystem: EncryptionSystem;
  onLeft: (
    reason: "user" | "timeout" | "decline" | "allOthersLeft" | "error",
  ) => void;
}

export const ActiveCall: FC<ActiveCallProps> = (props) => {
  const [vm, setVm] = useState<CallViewModel | null>(null);
  const [footerVm, setFooterVm] = useState<ViewModel<FooterSnapshot> | null>(
    null,
  );
  const urlParams = useUrlParams();
  const mediaDevices = useMediaDevices();
  const trackProcessorState$ = useTrackProcessorObservable$();

  useEffect(() => {
    rootLogger.info("START CALL VIEW SCOPE");
    const scope = new ObservableScope();
    const reactionsReader = new ReactionsReader(scope, props.rtcSession);
    const { autoLeaveWhenOthersLeft, waitForCallPickup, sendNotificationType } =
      urlParams;

    const vm = createCallViewModel$(
      scope,
      props.rtcSession,
      props.matrixRoom,
      mediaDevices,
      props.muteStates,
      {
        encryptionSystem: props.e2eeSystem,
        autoLeaveWhenOthersLeft,
        waitForCallPickup: waitForCallPickup && sendNotificationType === "ring",
        matrixRTCMode$: matrixRTCModeSetting.value$,
      },
      reactionsReader.raisedHands$,
      reactionsReader.reactions$,
      scope.behavior(trackProcessorState$),
    );

    vm.join();
    setVm(vm);

    vm.leave$.pipe(scope.bind()).subscribe(props.onLeft);

    let hasLeft = false;
    const handleTabClose = (): void => {
      if (hasLeft) return;
      hasLeft = true;

      const originalFetch = window.fetch.bind(window);
      window.fetch = (
        input: RequestInfo | URL,
        init?: RequestInit,
      ): Promise<Response> => {
        return originalFetch(input, { ...init, keepalive: true });
      };

      try {
        void props.rtcSession.leaveRoomSession(1000);
      } catch (e) {
        console.error("Ошибка leaveRoomSession:", e);
      }

      try {
        const userId = props.client.getUserId();
        const deviceId = props.client.getDeviceId();
        const accessToken = props.client.getAccessToken();
        const baseUrl = props.client.baseUrl;
        const roomId = props.matrixRoom.roomId;

        if (userId && accessToken && baseUrl && roomId) {
          const eventTypes = [
            "org.matrix.msc3401.call.member",
            "m.call.member",
          ];

          for (const eventType of eventTypes) {
            const stateEvents =
              props.matrixRoom.currentState.getStateEvents(eventType);

            for (const ev of stateEvents) {
              const stateKey = ev.getStateKey();
              const content = ev.getContent();
              if (!stateKey || !content || Object.keys(content).length === 0)
                continue;

              const isMyEvent =
                ev.getSender() === userId ||
                stateKey === userId ||
                stateKey.includes(userId);

              if (isMyEvent) {
                const isLegacy = Array.isArray(content.memberships);
                const remaining = isLegacy
                  ? content.memberships.filter(
                      (m: { device_id?: string }) => m.device_id !== deviceId,
                    )
                  : [];
                const nextContent =
                  isLegacy && remaining.length > 0
                    ? { memberships: remaining }
                    : {};

                const url = `${baseUrl.replace(/\/$/, "")}/_matrix/client/v3/rooms/${encodeURIComponent(roomId)}/state/${encodeURIComponent(eventType)}/${encodeURIComponent(stateKey)}`;
                originalFetch(url, {
                  method: "PUT",
                  headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify(nextContent),
                  keepalive: true,
                }).catch(() => {});
              }
            }
          }
        }
      } catch (e) {
        console.error("Ошибка прямой очистки стейта Matrix:", e);
      }

      try {
        const connList =
          (vm.allConnections$ as any).get?.() ??
          (vm.allConnections$ as any).value;
        const connections = connList?.getConnections?.() ?? [];
        for (const conn of connections) {
          conn.livekitRoom?.disconnect(true);
        }
      } catch (e) {}

      scope.end();
    };

    window.addEventListener("beforeunload", handleTabClose);
    window.addEventListener("pagehide", handleTabClose);

    return (): void => {
      window.removeEventListener("beforeunload", handleTabClose);
      window.removeEventListener("pagehide", handleTabClose);
      scope.end();
    };
  }, [
    props.rtcSession,
    props.matrixRoom,
    props.muteStates,
    props.e2eeSystem,
    props.onLeft,
    urlParams,
    mediaDevices,
    trackProcessorState$,
    props.client,
  ]);

  useEffect(() => {
    if (vm === null) return;
    const scope = new ObservableScope();
    const footerVm = createCallFooterViewModel(
      scope,
      vm,
      props.muteStates,
      mediaDevices,
      `${props.client.getUserId()}:${props.client.getDeviceId()}`,
    );
    setFooterVm(footerVm);
    return (): void => scope.end();
  }, [
    props.rtcSession,
    props.matrixRoom,
    props.muteStates,
    props.e2eeSystem,
    props.onLeft,
    urlParams,
    mediaDevices,
    trackProcessorState$,
    props.client,
    vm,
  ]);

  if (vm === null || footerVm === null) return null;

  return (
    <ReactionsSenderProvider vm={vm} rtcSession={props.rtcSession}>
      <InCallView {...props} vm={vm} footerVm={footerVm} />
    </ReactionsSenderProvider>
  );
};

export interface InCallViewProps {
  client: MatrixClient;
  vm: CallViewModel;
  footerVm: ViewModel<FooterSnapshot>;
  matrixInfo: MatrixInfo;
  rtcSession: MatrixRTCSession;
  matrixRoom: MatrixRoom;
  muteStates: MuteStates;
  onShareClick: (() => void) | null;
}

export const InCallView: FC<InCallViewProps> = ({
  client,
  vm,
  footerVm,
  matrixInfo,
  matrixRoom,
  muteStates,
  onShareClick,
}) => {
  const logger = useMemo(() => rootLogger.getChild("[InCallView]"), []);
  const { t, i18n } = useTranslation();
  const fullscreen = useCallFullscreen();
  useEffect(
    () => () => {
      void exitCallFullscreen();
    },
    [],
  );
  const { sendReaction, toggleRaisedHand } = useReactionsSender();

  const activeLang = i18n.resolvedLanguage || i18n.language || "en";
  const isRu = activeLang.toLowerCase().startsWith("ru");

  const uiText = useMemo(
    () => ({
      pipTitle: t("instance.in_call_view.pipTitle"),
      record: t("instance.in_call_view.record"),
      recording: t("instance.in_call_view.recording"),
      starting: t("instance.in_call_view.starting"),
      stopping: t("instance.in_call_view.stopping"),
      videoAndAudio: t("instance.in_call_view.videoAndAudio"),
      audioOnly: t("instance.in_call_view.audioOnly"),
      textOnly: t("instance.in_call_view.textOnly"),
      recordError: t("instance.in_call_view.recordError"),
      defaultParticipant: t("instance.in_call_view.defaultParticipant"),
      defaultRoomName: t("instance.in_call_view.defaultRoomName"),
      recentlySpoke: t("instance.in_call_view.recentlySpoke"),
      speakingNow: t("instance.in_call_view.speakingNow"),
      micMuted: t("instance.in_call_view.micMuted"),
      participantsCount: t("instance.in_call_view.participantsCount"),
      mediaSessionArtist: t("instance.in_call_view.mediaSessionArtist"),
      fallbackInitial: t("instance.in_call_view.fallbackInitial"),
      recordSettings: t("instance.in_call_view.recordSettings"),
      aiSummary: t("instance.in_call_view.aiSummary"),
      aiSummaryDesc: t("instance.in_call_view.aiSummaryDesc"),
      startRecording: t("instance.in_call_view.startRecording"),
      transcriptTitle: t("instance.in_call_view.transcriptTitle"),
      liveTranscript: t("instance.in_call_view.liveTranscript"),
      listeningParticipants: t("instance.in_call_view.listeningParticipants"),
      transcriptEmptyHint: t("instance.in_call_view.transcriptEmptyHint"),
      markImportant: t("instance.in_call_view.markImportant"),
    }),
    [t],
  );

  const uiTextRef = useRef(uiText);
  uiTextRef.current = uiText;

  const allConnections = useBehavior(vm.allConnections$);
  const livekitRoom = allConnections.getConnections()[0]?.livekitRoom;
  useAdaptiveVideoQuality(livekitRoom);

  // --- СТЕЙТЫ ЗАПИСИ (ТЕПЕРЬ ОБЪЕДИНЯЕМ ВИДЕО И ТЕКСТ) ---
  const [isGlobalRecording, setIsGlobalRecording] = useState(false); // LiveKit Video/Audio
  const [isTextRecording, setIsTextRecording] = useState(false); // Локальный стейт для текста
  const [isRecordingStarting, setIsRecordingStarting] = useState(false);
  const [showRecordMenu, setShowRecordMenu] = useState(false);
  const recordMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showRecordMenu) return;
    const outside = (event: Event): void => {
      if (event.target instanceof Node && !recordMenuRef.current?.contains(event.target))
        setShowRecordMenu(false);
    };
    const escape = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setShowRecordMenu(false);
        recordMenuRef.current?.querySelector("button")?.focus({ preventScroll: true });
      }
    };
    // Capture also sees taps on tiles whose handlers stop propagation.
    document.addEventListener("pointerdown", outside, true);
    document.addEventListener("click", outside, true);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside, true);
      document.removeEventListener("click", outside, true);
      document.removeEventListener("keydown", escape);
    };
  }, [showRecordMenu]);
  const [recordFormat, setRecordFormat] = useState<"video" | "audio" | "text">(
    "video",
  );
  const [enableAI, setEnableAI] = useState(AI_ENABLED);

  // Умный геттер для определения, идет ли любая запись
  const isAnyRecordingActive = isGlobalRecording || isTextRecording;

  const [showTranscriptPanel, setShowTranscriptPanel] = useState(false);
  const [transcripts, setTranscripts] = useState<any[]>([]);
  const [starredTimestamps, setStarredTimestamps] = useState<Set<number>>(
    new Set(),
  );
  const [isTranscriptionActive, setIsTranscriptionActive] = useState(false);
  const [currentRecordingId, setCurrentRecordingId] = useState<string | null>(
    null,
  );

  const transcriptScrollRef = useRef<HTMLDivElement>(null);
  const [canManageRecording, setCanManageRecording] = useState(false);
  useEffect(() => {
    if (!livekitRoom) return;
    let disposed = false;
    const sync = async () => {
      try {
        const query = new URLSearchParams({
          roomId: livekitRoom.name,
          matrixRoomId: matrixRoom.roomId,
        });
        const response = await backendFetch(
          client,
          `/api/recording/status?${query}`,
        );
        const status = await response.json();
        if (disposed) return;
        setCanManageRecording(status.canManage);
        setIsTextRecording(status.type === "text" && Boolean(status.status));
        setCurrentRecordingId(status.recordingId);
      } catch {
        /* Failed reads do not clear the active recording indicator. */
      }
    };
    void sync();
    const timer = window.setInterval(() => void sync(), 15000);
    livekitRoom.on(RoomEvent.RecordingStatusChanged, sync);
    livekitRoom.on(RoomEvent.Connected, sync);
    livekitRoom.on(RoomEvent.ParticipantConnected, sync);
    livekitRoom.on(RoomEvent.ParticipantDisconnected, sync);
    return () => {
      disposed = true;
      window.clearInterval(timer);
      livekitRoom.off(RoomEvent.RecordingStatusChanged, sync);
      livekitRoom.off(RoomEvent.Connected, sync);
      livekitRoom.off(RoomEvent.ParticipantConnected, sync);
      livekitRoom.off(RoomEvent.ParticipantDisconnected, sync);
    };
  }, [client, livekitRoom, matrixRoom.roomId]);

  const getDisplayName = useCallback(
    (participantId: string) => {
      if (!matrixRoom) return participantId;
      const member = matrixRoom
        .getMembers()
        .find((m) => m.userId.includes(participantId));
      if (member && member.name)
        return accountDisplayName(member.userId, member.name);
      return participantId.charAt(0).toUpperCase() + participantId.slice(1);
    },
    [matrixRoom],
  );

  const getNamesMap = useCallback(() => {
    const map: Record<string, string> = {};
    if (matrixRoom && typeof matrixRoom.getMembers === "function") {
      matrixRoom.getMembers().forEach((m) => {
        if (m && m.userId) {
          const cleanId = m.userId.split(":")[0].replace("@", "");
          map[cleanId] = accountDisplayName(
            m.userId,
            m.rawDisplayName || m.name || cleanId,
          );
        }
      });
    }
    return map;
  }, [matrixRoom]);

  const toggleStar = (timestamp: number) => {
    if (!timestamp) return;
    setStarredTimestamps((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(timestamp)) {
        newSet.delete(timestamp);
      } else {
        newSet.add(timestamp);
        if (livekitRoom?.localParticipant) {
          const payload = JSON.stringify({
            type: "command",
            action: "star",
            timestamp,
          });
          try {
            void backendFetch(client, "/api/recording/command", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                ...JSON.parse(payload),
                recordingId: currentRecordingId,
              }),
            }).catch(console.error);
          } catch (e) {}
        }
      }
      return newSet;
    });
  };

  const toggleTranscription = useCallback(
    async (start: boolean, recordingId?: string) => {
      if (
        !AI_ENABLED ||
        !livekitRoom?.localParticipant ||
        !(recordingId || currentRecordingId)
      )
        return;

      const currentLang = isRu ? "ru" : "en";
      const payload = {
        type: "command",
        action: start ? "start_transcription" : "stop_transcription",
        recordingId: recordingId,
        lang: currentLang,
        names: getNamesMap(),
      };

      try {
        await backendFetch(client, "/api/recording/command", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...payload,
            recordingId: recordingId || currentRecordingId,
          }),
        });
      } catch (err) {}
    },
    [livekitRoom, isRu, getNamesMap, client, currentRecordingId],
  );

  useEffect(() => {
    if (transcriptScrollRef.current) {
      transcriptScrollRef.current.scrollTop =
        transcriptScrollRef.current.scrollHeight;
    }
  }, [transcripts, showTranscriptPanel]);

  useEffect(() => {
    if (!livekitRoom) return;

    setIsGlobalRecording(livekitRoom.isRecording);

    const handleRecordingChange = (isRecording: boolean) => {
      setIsGlobalRecording(isRecording);
      setIsRecordingStarting(false);

      if (!isRecording && !isTextRecording) {
        setIsTranscriptionActive(false);
        setCurrentRecordingId(null);
        toggleTranscription(false);
      }
    };

    livekitRoom.on(RoomEvent.RecordingStatusChanged, handleRecordingChange);
    return () => {
      livekitRoom.off(RoomEvent.RecordingStatusChanged, handleRecordingChange);
    };
  }, [livekitRoom, isTextRecording, toggleTranscription]);

  useEffect(() => {
    if (!livekitRoom) return;

    const handleDataReceived = (payload: Uint8Array) => {
      try {
        const decodedString = new TextDecoder().decode(payload);
        const data = JSON.parse(decodedString);

        if (data.type === "transcript") {
          const participantName = data.participant
            .split(":")[0]
            .replace("@", "");

          setTranscripts((prev) => {
            const newTranscripts = [...prev];
            const activeIndex = newTranscripts.findIndex(
              (t) => t.participant === participantName && !t.isFinal,
            );

            if (data.status === "interim") {
              if (activeIndex >= 0)
                newTranscripts[activeIndex].text = data.text;
              else
                newTranscripts.push({
                  id: Date.now().toString(),
                  participant: participantName,
                  text: data.text,
                  isFinal: false,
                });
            } else if (data.status === "final") {
              if (activeIndex >= 0) {
                newTranscripts[activeIndex].text = data.text;
                newTranscripts[activeIndex].isFinal = true;
                newTranscripts[activeIndex].timestamp = data.timestamp;
              } else {
                newTranscripts.push({
                  id: data.timestamp
                    ? data.timestamp.toString()
                    : Date.now().toString(),
                  participant: participantName,
                  text: data.text,
                  isFinal: true,
                  timestamp: data.timestamp,
                });
              }
            }
            return newTranscripts;
          });
        }
      } catch (e) {}
    };

    livekitRoom.on(RoomEvent.DataReceived, handleDataReceived);
    return () => {
      livekitRoom.off(RoomEvent.DataReceived, handleDataReceived);
    };
  }, [livekitRoom]);

  // ❗️ ИСПРАВЛЕННАЯ ЛОГИКА ЗАПИСИ
  const toggleRecording = async (
    type: "video" | "audio" | "text" | any = "video",
  ) => {
    const recordType = typeof type === "string" ? type : "video";
    if (
      !livekitRoom?.localParticipant ||
      isRecordingStarting ||
      !canManageRecording
    )
      return;

    const userId = livekitRoom.localParticipant.identity;
    const roomId = livekitRoom.name;

    setIsRecordingStarting(true);
    setShowRecordMenu(false);

    try {
      const endpoint = isAnyRecordingActive ? "stop" : "start";
      const res = await backendFetch(client, `/api/recording/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          roomId,
          matrixRoomId: matrixRoom.roomId,
          recordingId: currentRecordingId,
          userId,
          type: recordType,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (endpoint === "start" && data.success && data.recordingId) {
        setTranscripts([]);
        setCurrentRecordingId(data.recordingId);

        // Включаем стейт текста мгновенно, так как LiveKit не пришлет событие
        if (recordType === "text") {
          setIsTextRecording(true);
          setIsRecordingStarting(false);
        }

        if (enableAI || recordType === "text") {
          setIsTranscriptionActive(true);
          toggleTranscription(true, data.recordingId);
          setShowTranscriptPanel(true);
        }
      } else if (endpoint === "stop") {
        // Мгновенно тушим текстовую запись
        setIsTextRecording(false);
        setIsRecordingStarting(false);
        setIsTranscriptionActive(false);
        setCurrentRecordingId(null);
        toggleTranscription(false);
      }
    } catch (e) {
      setIsRecordingStarting(false);
      alert(localizedRequestError(e, t));
    }
  };

  useWakeLock();

  const containerRef1 = useRef<HTMLDivElement | null>(null);
  const [containerRef2, bounds] = useMeasure();
  const containerRef = useMergedRefs(containerRef1, containerRef2);
  const { showControls, header: headerStyle } = useUrlParams();
  const muteAllAudio = useBehavior(muteAllAudio$);
  const toggleAudio = useBehavior(muteStates.audio.toggle$);
  const toggleVideo = useBehavior(muteStates.video.toggle$);
  const setAudioEnabled = useBehavior(muteStates.audio.setEnabled$);
  const audioEnabled = useBehavior(muteStates.audio.enabled$);
  const videoEnabled = useBehavior(muteStates.video.enabled$);

  useCallViewKeyboardShortcuts(
    toggleAudio,
    toggleVideo,
    setAudioEnabled,
    (reaction) => void sendReaction(reaction),
    () => void toggleRaisedHand(),
  );

  const ringingVm = useBehavior(vm.ringingVm$);
  const audioParticipants = useBehavior(vm.livekitRoomItems$);
  useScreenShareOrientationRooms(
    audioParticipants.map((item) => item.livekitRoom),
  );
  const participantCount = useBehavior(vm.participantCount$);
  const reconnecting = useBehavior(vm.reconnecting$);
  const layout = useBehavior(vm.layout$);
  const fullscreenSpotlight =
    layout.type === "grid" ||
    layout.type === "spotlight-landscape" ||
    layout.type === "spotlight-portrait" ||
    layout.type === "spotlight-expanded" ||
    layout.type === "one-on-one-portrait" ||
    layout.type === "pip"
      ? layout.spotlight
      : undefined;
  const [fullscreenMediaId, setFullscreenMediaId] = useState<
    string | undefined
  >();
  const requestMediaFullscreen = useCallback((mediaId?: string): void => {
    setFullscreenMediaId(mediaId);
    void toggleCallFullscreen();
  }, []);
  useEffect(() => {
    if (!fullscreen) setFullscreenMediaId(undefined);
  }, [fullscreen]);
  const [spotlightHasScreenShare, setSpotlightHasScreenShare] = useState(false);
  useEffect(() => {
    if (!fullscreenSpotlight) {
      setSpotlightHasScreenShare(false);
      return;
    }
    const subscription = fullscreenSpotlight.media$.subscribe((media) =>
      setSpotlightHasScreenShare(
        media.some((item) => item.type === "screen share"),
      ),
    );
    return () => subscription.unsubscribe();
  }, [fullscreenSpotlight]);
  const overlayControls = fullscreen && spotlightHasScreenShare;
  const edgeToEdge = useBehavior(vm.edgeToEdge$);
  const overflowing = useBehavior(vm.overflowing$);
  const showNameTags = useBehavior(vm.showNameTags$);
  const showHeader = useBehavior(vm.showHeader$);
  const settingsOpen = useBehavior(vm.settingsOpen$);
  const setSettingsOpen = useBehavior(vm.setSettingsOpen$);
  const earpieceMode = useBehavior(vm.earpieceMode$);
  const audioOutputSwitcher = useBehavior(vm.audioOutputSwitcher$);

  const pipVideoRef = useRef<ExtendedVideoElement | null>(null);
  const [isPipActive, setIsPipActive] = useState(false);
  const [pipError, setPipError] = useState<string | null>(null);
  const dismissPipError = useCallback(() => setPipError(null), []);

  const lastSpeakerIdentityRef = useRef<string | null>(null);
  const livekitRoomRef = useRef(livekitRoom);
  livekitRoomRef.current = livekitRoom;
  const participantCountRef = useRef(participantCount);
  participantCountRef.current = participantCount;
  const roomNameRef = useRef(matrixInfo.roomName);
  roomNameRef.current = matrixInfo.roomName;
  const localDisplayNameRef = useRef(matrixInfo.displayName);
  localDisplayNameRef.current = matrixInfo.displayName;

  useEffect(() => {
    if (!livekitRoom) return;
    const onActiveSpeakersChanged = (speakers: Participant[]): void => {
      const humanSpeakers = speakers.filter(isHumanParticipant);
      if (humanSpeakers.length === 0) return;
      const preferred =
        humanSpeakers.find((p) => !p.isLocal) ?? humanSpeakers[0];
      if (preferred?.identity)
        lastSpeakerIdentityRef.current = preferred.identity;
    };
    livekitRoom.on(RoomEvent.ActiveSpeakersChanged, onActiveSpeakersChanged);
    return (): void => {
      livekitRoom.off(RoomEvent.ActiveSpeakersChanged, onActiveSpeakersChanged);
    };
  }, [livekitRoom]);

  const resolveParticipantName = useCallback(
    (participant: Participant | undefined): string => {
      const fallbackName = uiTextRef.current.defaultParticipant;
      if (!participant) return localDisplayNameRef.current || fallbackName;
      if (participant.isLocal && localDisplayNameRef.current)
        return localDisplayNameRef.current;
      if (participant.name && !participant.name.startsWith("@"))
        return participant.name;
      const id = participant.identity || "";
      if (id.startsWith("@")) {
        const parts = id.split(":");
        const matrixUserId = parts.length >= 2 ? `${parts[0]}:${parts[1]}` : id;
        const member = matrixRoom.getMember(matrixUserId);
        if (member?.rawDisplayName) return member.rawDisplayName;
        if (member?.name) return accountDisplayName(matrixUserId, member.name);
        return localUsername(matrixUserId);
      }
      return id.split(":")[0] || fallbackName;
    },
    [matrixRoom],
  );

  const findParticipantVideoElement = useCallback(
    (participant: Participant | undefined): HTMLVideoElement | null => {
      if (participant) {
        const pubs = (participant as any).videoTrackPublications;
        if (pubs) {
          for (const pub of pubs.values()) {
            if (!pub.isMuted && pub.track?.attachedElements) {
              for (const el of pub.track.attachedElements) {
                if (
                  el instanceof HTMLVideoElement &&
                  el !== pipVideoRef.current &&
                  el.readyState >= 2 &&
                  el.videoWidth > 0
                )
                  return el;
              }
            }
          }
        }
        // A camera-less speaker must keep their avatar, not another person's video.
        return null;
      }
      const container = containerRef1.current;
      if (!container) return null;
      const videos = Array.from(container.querySelectorAll("video")).filter(
        (v) =>
          v !== pipVideoRef.current &&
          v.readyState >= 2 &&
          v.videoWidth > 0 &&
          !v.paused,
      );
      if (videos.length === 0) return null;
      videos.sort((a, b) => {
        const rectA = a.getBoundingClientRect();
        const rectB = b.getBoundingClientRect();
        return rectB.width * rectB.height - rectA.width * rectA.height;
      });
      return videos[0];
    },
    [],
  );

  useEffect(() => {
    const pipVideo = pipVideoRef.current;
    if (!pipVideo) return;
    const canvas = document.createElement("canvas");
    canvas.width = 640;
    canvas.height = 360;
    const ctx = canvas.getContext("2d");
    let pulsePhase = 0;

    const drawSpeakerTile = (): void => {
      if (!ctx) return;
      pulsePhase += 0.25;
      const labels = uiTextRef.current;
      const room = livekitRoomRef.current;
      let targetParticipant: Participant | undefined;
      let humanCount = participantCountRef.current;

      if (room) {
        const remotes = Array.from(room.remoteParticipants.values()).filter(
          isHumanParticipant,
        );
        const allParticipants: Participant[] = [
          ...remotes,
          room.localParticipant,
        ];
        humanCount = allParticipants.length;

        const speakingNow =
          remotes.find((p) => p.isSpeaking) ??
          (room.localParticipant.isSpeaking
            ? room.localParticipant
            : undefined);
        if (speakingNow) {
          targetParticipant = speakingNow;
          lastSpeakerIdentityRef.current = speakingNow.identity;
        } else if (lastSpeakerIdentityRef.current) {
          targetParticipant = allParticipants.find(
            (p) => p.identity === lastSpeakerIdentityRef.current,
          );
        }
        if (!targetParticipant)
          targetParticipant = remotes[0] ?? room.localParticipant;
      }

      const presentation = room
        ? visibleScreenShare([
            ...Array.from(room.remoteParticipants.values()).filter(
              isHumanParticipant,
            ),
            room.localParticipant,
          ])
        : null;
      const video =
        presentation?.video ?? findParticipantVideoElement(targetParticipant);
      const dimensions = pictureInPictureSize(video, Boolean(presentation));
      if (
        canvas.width !== dimensions.width ||
        canvas.height !== dimensions.height
      ) {
        canvas.width = dimensions.width;
        canvas.height = dimensions.height;
      }
      const speakerName = resolveParticipantName(targetParticipant);
      renderPictureInPictureFrame(ctx, {
        width: canvas.width,
        height: canvas.height,
        roomName: roomNameRef.current || labels.defaultRoomName,
        participantCount: humanCount,
        speakerName,
        isSpeaking: Boolean(targetParticipant?.isSpeaking),
        isMicEnabled: Boolean(targetParticipant?.isMicrophoneEnabled),
        video,
        screenShare: Boolean(presentation),
        pulsePhase,
        labels,
      });
    };

    let canvasStream: MediaStream | null = null;
    try {
      if (typeof canvas.captureStream === "function") {
        canvasStream = canvas.captureStream(15);
        pipVideo.srcObject = canvasStream;
      }
    } catch {
      logger.info("PiP canvas stream is unavailable");
    }

    drawSpeakerTile();
    let worker: Worker | null = null;
    let workerUrl: string | null = null;
    let fallbackInterval: number | null = null;
    try {
      const blob = new Blob(["setInterval(() => postMessage(0), 120);"], {
        type: "application/javascript",
      });
      workerUrl = URL.createObjectURL(blob);
      worker = new Worker(workerUrl);
      worker.onmessage = (): void => drawSpeakerTile();
    } catch {
      fallbackInterval = window.setInterval(drawSpeakerTile, 120);
    }

    pipVideo.autoPictureInPicture = true;
    pipVideo.setAttribute("autopictureinpicture", "");
    pipVideo.disablePictureInPicture = false;
    pipVideo.removeAttribute("disablepictureinpicture");
    pipVideo.play().catch(() => {});

    return (): void => {
      worker?.terminate();
      if (workerUrl) URL.revokeObjectURL(workerUrl);
      if (fallbackInterval !== null) window.clearInterval(fallbackInterval);
      canvasStream?.getTracks().forEach((track) => track.stop());
      if (pipVideo.srcObject === canvasStream) pipVideo.srcObject = null;
    };
  }, [resolveParticipantName, findParticipantVideoElement, logger]);

  const togglePiP = useCallback(async (): Promise<void> => {
    setPipError(null);
    if (document.pictureInPictureElement) {
      await document.exitPictureInPicture().catch(() => {});
      setIsPipActive(false);
      return;
    }

    // Always use the composed stream: raw camera video loses names, count and VAD.
    const targetVideo = pipVideoRef.current;
    if (!targetVideo) return;

    try {
      targetVideo.disablePictureInPicture = false;
      targetVideo.removeAttribute("disablepictureinpicture");
      // Keep the PiP request in the click's activation; awaiting play() can
      // exhaust activation while the browser waits for a frame.
      if (targetVideo.paused) void targetVideo.play().catch(() => {});

      if (
        typeof targetVideo.webkitSupportsPresentationMode === "function" &&
        typeof targetVideo.webkitSetPresentationMode === "function" &&
        targetVideo.webkitSupportsPresentationMode("picture-in-picture")
      ) {
        if (targetVideo.webkitPresentationMode === "picture-in-picture") {
          targetVideo.webkitSetPresentationMode("inline");
          setIsPipActive(false);
        } else {
          targetVideo.webkitSetPresentationMode("picture-in-picture");
          setIsPipActive(true);
        }
        return;
      }

      await requestPictureInPicture(
        targetVideo,
        document.pictureInPictureEnabled,
      );
      setIsPipActive(true);
    } catch (err) {
      const name =
        err instanceof Error || err instanceof DOMException
          ? err.name
          : "UnknownError";
      // Log only the error type: call links and media details stay private.
      logger.warn("PiP request failed", name);
      setPipError(
        name === "NotSupportedError"
          ? t("instance.in_call_view.pip_unsupported")
          : name === "InvalidStateError"
            ? t("instance.in_call_view.pip_not_ready")
            : t("instance.in_call_view.pip_denied"),
      );
    }
  }, [t, logger]);

  useEffect(() => {
    const onEnterPiP = (): void => setIsPipActive(true);
    const onLeavePiP = (): void => setIsPipActive(false);
    const onSafariPresentationChange = (event: Event): void => {
      const video = event.target as ExtendedVideoElement | null;
      if (video instanceof HTMLVideoElement) {
        setIsPipActive(video.webkitPresentationMode === "picture-in-picture");
      }
    };
    document.addEventListener(
      "webkitpresentationmodechanged",
      onSafariPresentationChange,
      true,
    );
    document.addEventListener("enterpictureinpicture", onEnterPiP, true);
    document.addEventListener("leavepictureinpicture", onLeavePiP, true);
    return (): void => {
      document.removeEventListener(
        "webkitpresentationmodechanged",
        onSafariPresentationChange,
        true,
      );
      document.removeEventListener("enterpictureinpicture", onEnterPiP, true);
      document.removeEventListener("leavepictureinpicture", onLeavePiP, true);
    };
  }, []);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const ms = navigator.mediaSession as ExtendedMediaSession;

    try {
      ms.metadata = new MediaMetadata({
        title: matrixInfo.roomName || uiText.defaultRoomName,
        artist: uiText.mediaSessionArtist,
      });
      ms.playbackState = "playing";
    } catch {}

    const safeSetHandler = (
      action: string,
      handler: MediaSessionActionHandler | null,
    ): void => {
      try {
        ms.setActionHandler(action as MediaSessionAction, handler);
      } catch {}
    };

    safeSetHandler("enterpictureinpicture", () => void togglePiP());
    safeSetHandler("togglemicrophone", () => toggleAudio?.());
    safeSetHandler("togglecamera", () => toggleVideo?.());
    safeSetHandler("hangup", () => {
      if (document.pictureInPictureElement)
        document.exitPictureInPicture().catch(() => {});
      vm.hangup();
    });

    return (): void => {
      safeSetHandler("enterpictureinpicture", null);
      safeSetHandler("togglemicrophone", null);
      safeSetHandler("togglecamera", null);
      safeSetHandler("hangup", null);
    };
  }, [togglePiP, toggleAudio, toggleVideo, vm, matrixInfo.roomName, uiText]);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const ms = navigator.mediaSession as ExtendedMediaSession;
    try {
      ms.setMicrophoneActive?.(audioEnabled);
      ms.setCameraActive?.(videoEnabled);
    } catch {}
  }, [audioEnabled, videoEnabled]);

  const fatalCallError = useBehavior(vm.fatalError$);
  if (fatalCallError) throw fatalCallError;

  const touchStart = useRef<{ id: number; x: number; y: number } | null>(null);
  const onViewPointerDown = useCallback((e: ReactPointerEvent) => {
    if (e.pointerType === "touch")
      touchStart.current = { id: e.pointerId, x: e.clientX, y: e.clientY };
  }, []);
  const onViewPointerUp = useCallback(
    (e: ReactPointerEvent) => {
      const start = touchStart.current;
      touchStart.current = null;
      if (
        e.pointerType === "touch" &&
        start?.id === e.pointerId &&
        Math.hypot(e.clientX - start.x, e.clientY - start.y) <= 10 &&
        (e.target as Element).closest?.(
          "button, input, textarea, [data-call-chat]",
        ) === null
      )
        vm.tapScreen();
    },
    [vm],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent) => {
      if (e.pointerType === "mouse") vm.hoverScreen();
    },
    [vm],
  );

  const onPointerOut = useCallback(() => vm.unhoverScreen(), [vm]);

  const [settingsTab, setSettingsTab] = useState(defaultSettingsTab);

  const openProfile = useMemo(
    () =>
      widget === null
        ? (): void => {
            setSettingsTab("profile");
            setSettingsOpen(true);
          }
        : null,
    [setSettingsTab, setSettingsOpen],
  );

  const [headerRef, headerBounds] = useMeasure();
  const [footerRef, footerBounds] = useMeasure();

  const gridBounds = useMemo(
    () => ({
      width: bounds.width,
      height:
        bounds.height -
        (edgeToEdge
          ? 0
          : (fullscreen ? 0 : headerBounds.height) +
            (overlayControls ? 0 : footerBounds.height)),
    }),
    [
      bounds.width,
      bounds.height,
      headerBounds.height,
      fullscreen,
      overlayControls,
      footerBounds.height,
      edgeToEdge,
    ],
  );
  const gridBoundsObservable$ = useObservable(
    (inputs$) => inputs$.pipe(map(([gridBounds]) => gridBounds)),
    [gridBounds],
  );

  useAppBarHidden(!showHeader || fullscreen);
  useAppBarSubtitle(
    ringingVm && vm.ringingStatusLocation === "app_bar" && (
      <RingingStatus vm={ringingVm} />
    ),
  );

  let header: ReactNode = null;
  switch (headerStyle) {
    case HeaderStyle.AppBar:
      break;
    case HeaderStyle.None:
      header = showHeader && !fullscreen && (
        <div
          className={classNames(styles.header, styles.filler)}
          ref={headerRef}
        />
      );
      break;
    case HeaderStyle.Standard:
      header = (
        <Header
          className={classNames(styles.header, {
            [styles.overlay]: edgeToEdge,
            [styles.hidden]: !showHeader || fullscreen,
          })}
          ref={headerRef}
          disconnectedBanner={false}
        >
          <LeftNav>
            <RoomHeaderInfo
              id={matrixInfo.roomId}
              name={matrixInfo.roomName}
              avatarUrl={matrixInfo.roomAvatar}
              encrypted={livekitRoom?.isE2EEEnabled ?? false}
              participantCount={participantCount}
            />
          </LeftNav>
          <RightNav>
            <button
              className="glass-button"
              onClick={() => void togglePiP()}
              title={uiText.pipTitle}
              aria-label={uiText.pipTitle}
              aria-pressed={isPipActive}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "8px 10px",
                borderRadius: "999px",
                border: isPipActive
                  ? "1px solid rgba(52, 199, 89, 0.6)"
                  : "1px solid var(--glass-edge)",
                backgroundColor: isPipActive
                  ? "rgba(52, 199, 89, 0.15)"
                  : "var(--glass-fill)",
                color: isPipActive ? "#34c759" : "var(--app-text)",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <rect
                  x="12"
                  y="9"
                  width="8"
                  height="6"
                  rx="1"
                  fill="currentColor"
                />
              </svg>
            </button>

            <div ref={recordMenuRef} style={{ position: "relative" }}>
              {/* ❗️ ИСПРАВЛЕННАЯ КНОПКА ЗАПИСИ */}
              <button
                className="glass-button"
                onClick={() => {
                  if (isAnyRecordingActive) toggleRecording();
                  else setShowRecordMenu(!showRecordMenu);
                }}
                disabled={isRecordingStarting || !canManageRecording}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 14px",
                  borderRadius: "999px",
                  border: isAnyRecordingActive
                    ? "1px solid rgba(255, 59, 48, 0.5)"
                    : "1px solid var(--glass-edge)",
                  backgroundColor: isAnyRecordingActive
                    ? "rgba(255, 59, 48, 0.1)"
                    : "var(--glass-fill)",
                  color: isAnyRecordingActive ? "#ff3b30" : "var(--app-text)",
                  cursor: isRecordingStarting ? "wait" : "pointer",
                  fontWeight: 600,
                  fontSize: "14px",
                  transition: "all 0.2s ease",
                  opacity: isRecordingStarting ? 0.7 : 1,
                }}
              >
                {isRecordingStarting ? (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ animation: "spin 1s linear infinite" }}
                  >
                    <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" />
                  </svg>
                ) : (
                  <div
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      backgroundColor: isAnyRecordingActive
                        ? "#ff3b30"
                        : "currentColor",
                      boxShadow: isAnyRecordingActive
                        ? "0 0 8px #ff3b30"
                        : "none",
                      animation: isAnyRecordingActive
                        ? "pulse 2s infinite"
                        : "none",
                    }}
                  />
                )}
                {isRecordingStarting
                  ? isAnyRecordingActive
                    ? uiText.stopping
                    : uiText.starting
                  : isAnyRecordingActive
                    ? uiText.recording
                    : uiText.record}
              </button>

              {showRecordMenu &&
                !isAnyRecordingActive &&
                !isRecordingStarting && (
                  <div
                    role="group"
                    aria-label={uiText.recordSettings}
                    style={{
                      position: "absolute",
                      top: "120%",
                      right: "8px",
                      background: "var(--app-modal)",
                      backdropFilter: "blur(24px)",
                      WebkitBackdropFilter: "blur(24px)",
                      maxWidth: "calc(100vw - 32px)",
                      boxSizing: "border-box",
                      border: "1px solid var(--glass-edge)",
                      borderRadius: "24px",
                      padding: "16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "16px",
                      zIndex: 1000,
                      boxShadow: "0 12px 40px var(--glass-shadow)",
                      minWidth: "260px",
                      color: "var(--app-text)",
                      fontFamily: "sans-serif",
                    }}
                  >
                    <h4
                      style={{
                        margin: 0,
                        fontSize: "15px",
                        fontWeight: 600,
                        color: "var(--app-text)",
                      }}
                    >
                      {uiText.recordSettings}
                    </h4>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          cursor: "pointer",
                          fontSize: "14px",
                          color: "var(--app-muted)",
                        }}
                      >
                        <input
                          type="radio"
                          name="recordFormat"
                          checked={recordFormat === "video"}
                          onChange={() => setRecordFormat("video")}
                          style={{
                            accentColor: "#0a84ff",
                            width: "16px",
                            height: "16px",
                            cursor: "pointer",
                          }}
                        />
                        {uiText.videoAndAudio}
                      </label>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          cursor: "pointer",
                          fontSize: "14px",
                          color: "var(--app-muted)",
                        }}
                      >
                        <input
                          type="radio"
                          name="recordFormat"
                          checked={recordFormat === "audio"}
                          onChange={() => setRecordFormat("audio")}
                          style={{
                            accentColor: "#0a84ff",
                            width: "16px",
                            height: "16px",
                            cursor: "pointer",
                          }}
                        />
                        {uiText.audioOnly}
                      </label>
                      {AI_ENABLED && (
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            cursor: "pointer",
                            fontSize: "14px",
                            color: "var(--app-muted)",
                          }}
                        >
                          <input
                            type="radio"
                            name="recordFormat"
                            checked={recordFormat === "text"}
                            onChange={() => {
                              setRecordFormat("text");
                              setEnableAI(true);
                            }}
                            style={{
                              accentColor: "#0a84ff",
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                            }}
                          />
                          {uiText.textOnly}
                        </label>
                      )}
                    </div>

                    <hr
                      style={{
                        border: "none",
                        borderTop: "1px solid var(--glass-edge)",
                        margin: "0",
                      }}
                    />

                    {AI_ENABLED && (
                      <label
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "10px",
                          cursor:
                            recordFormat === "text" ? "not-allowed" : "pointer",
                          opacity: recordFormat === "text" ? 0.5 : 1,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={enableAI}
                          disabled={recordFormat === "text"}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setEnableAI(checked);
                            if (isAnyRecordingActive && currentRecordingId) {
                              toggleTranscription(checked, currentRecordingId);
                              setShowTranscriptPanel(checked);
                            }
                          }}
                          style={{
                            width: "16px",
                            height: "16px",
                            accentColor: "#0a84ff",
                            cursor:
                              recordFormat === "text"
                                ? "not-allowed"
                                : "pointer",
                            marginTop: "2px",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "2px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              color: enableAI
                                ? "var(--app-text)"
                                : "var(--app-muted)",
                            }}
                          >
                            {uiText.aiSummary}
                          </span>
                          <span style={{ fontSize: "12px", color: "#777" }}>
                            {uiText.aiSummaryDesc}
                          </span>
                        </div>
                      </label>
                    )}

                    <button
                      className="glass-button"
                      onClick={() => {
                        toggleRecording(recordFormat);
                        setShowRecordMenu(false);
                      }}
                      style={{
                        background: "var(--glass-accent)",
                        color: "var(--app-text)",
                        border: "none",
                        padding: "12px",
                        borderRadius: "999px",
                        fontWeight: "bold",
                        cursor: "pointer",
                        fontSize: "14px",
                        transition: "background 0.2s",
                      }}
                      onMouseOver={(e) =>
                        (e.currentTarget.style.background =
                          "var(--glass-hover)")
                      }
                      onMouseOut={(e) =>
                        (e.currentTarget.style.background =
                          "var(--glass-accent)")
                      }
                    >
                      {uiText.startRecording}
                    </button>
                  </div>
                )}
            </div>

            {AI_ENABLED && (
              <button
                className="glass-button"
                onClick={() => setShowTranscriptPanel(!showTranscriptPanel)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "8px",
                  borderRadius: "999px",
                  border: showTranscriptPanel
                    ? "1px solid rgba(255, 255, 255, 0.8)"
                    : "1px solid var(--glass-edge)",
                  backgroundColor: showTranscriptPanel
                    ? "rgba(255, 255, 255, 0.2)"
                    : "var(--glass-fill)",
                  color: "var(--app-text)",
                  cursor: "pointer",
                  marginRight: "8px",
                  transition: "all 0.2s ease",
                }}
                title={uiText.transcriptTitle}
                aria-label={uiText.transcriptTitle}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  <line x1="9" y1="9" x2="15" y2="9"></line>
                  <line x1="9" y1="13" x2="15" y2="13"></line>
                </svg>
              </button>
            )}

            {showControls && onShareClick !== null && (
              <InviteButton className={styles.invite} onClick={onShareClick} />
            )}
          </RightNav>
        </Header>
      );
      break;
  }

  const onDismissReconnectingToast = useCallback(() => {}, []);
  const reconnectingToast = (
    <>
      <div
        className={classNames(overlayStyles.bg, overlayStyles.animate)}
        data-state={reconnecting ? "open" : "closed"}
      />
      <Toast
        onDismiss={onDismissReconnectingToast}
        open={reconnecting}
        modal={false}
      >
        {t("common.reconnecting")}
      </Toast>
    </>
  );

  const earpieceOverlay = (
    <EarpieceOverlay
      show={earpieceMode && !reconnecting}
      onBackToVideoPressed={audioOutputSwitcher?.switch}
    />
  );
  const contentObscured = reconnecting || earpieceMode;

  const Tile = useMemo(
    () =>
      function Tile({
        ref,
        className,
        style,
        targetWidth,
        targetHeight,
        model,
      }: TileProps<TileViewModel, HTMLDivElement>): ReactNode {
        const spotlightExpanded = useBehavior(vm.spotlightExpanded$);
        const onToggleExpanded = useBehavior(vm.toggleSpotlightExpanded$);
        const showSpotlightIndicators = useBehavior(
          vm.showSpotlightIndicators$,
        );
        const showSpeakingIndicators = useBehavior(vm.showSpeakingIndicators$);
        const showNameTags = useBehavior(vm.showNameTags$);
        const showRingingStatus = vm.ringingStatusLocation === "tile";
        const showOutline = useBehavior(
          model instanceof GridTileViewModel
            ? model.showOutline$
            : constant(false),
        );

        return model instanceof GridTileViewModel ? (
          <GridTile
            ref={ref}
            vm={model}
            onOpenProfile={openProfile}
            targetWidth={targetWidth}
            targetHeight={targetHeight}
            className={classNames(className, styles.tile)}
            style={style}
            showSpeakingIndicators={showSpeakingIndicators}
            showNameTags={showNameTags}
            showRingingStatus={showRingingStatus}
            showOutline={showOutline}
            focusable={!contentObscured}
          />
        ) : (
          <SpotlightTile
            ref={ref}
            vm={model}
            expanded={spotlightExpanded}
            onRequestFullscreen={requestMediaFullscreen}
            onToggleExpanded={onToggleExpanded}
            targetWidth={targetWidth}
            targetHeight={targetHeight}
            showIndicators={showSpotlightIndicators}
            showNameTags={showNameTags}
            showRingingStatus={showRingingStatus}
            focusable={!contentObscured}
            className={classNames(className, styles.tile)}
            itemClassName={styles.spotlightItem}
            style={style}
          />
        );
      },
    [vm, openProfile, contentObscured, requestMediaFullscreen],
  );

  const layouts = useMemo(() => {
    const inputs = { minBounds$: gridBoundsObservable$ };
    return {
      grid: makeGridLayout(inputs),
      "spotlight-landscape": makeSpotlightLandscapeLayout(inputs),
      "spotlight-portrait": makeSpotlightPortraitLayout(inputs),
      "spotlight-expanded": makeSpotlightExpandedLayout(inputs),
      "one-on-one-landscape": makeOneOnOneLandscapeLayout(inputs),
      "one-on-one-portrait": makeOneOnOnePortraitLayout(inputs),
    };
  }, [gridBoundsObservable$]);

  const showFooter = useBehavior(footerVm.showFooter$);
  const renderContent = (): JSX.Element => {
    if (
      layout.type === "pip" ||
      (fullscreen && spotlightHasScreenShare && fullscreenSpotlight)
    )
      return (
        <SpotlightTile
          className={classNames(styles.tile, {
            [styles.fullscreenMedia]: fullscreen,
          })}
          itemClassName={styles.spotlightItem}
          data-maximised
          vm={fullscreenSpotlight!}
          initialVisibleId={fullscreenMediaId}
          style={{ "--call-fullscreen-controls-height": `${showFooter ? footerBounds.height : 0}px` } as ComponentProps<typeof SpotlightTile>["style"]}
          onRequestFullscreen={requestMediaFullscreen}
          expanded
          onToggleExpanded={null}
          targetWidth={gridBounds.width}
          targetHeight={gridBounds.height}
          showIndicators={false}
          showNameTags={showNameTags}
          showRingingStatus={vm.ringingStatusLocation === "tile"}
          focusable={!contentObscured}
          aria-hidden={contentObscured}
        />
      );

    const layers = layouts[layout.type] as CallLayoutOutputs<Layout>;
    const fixedGrid = (
      <Grid
        key="fixed"
        className={classNames(styles.fixedGrid, {
          [styles.foregroundGrid]: layers.foreground === "fixed",
        })}
        style={{
          insetBlockStart: edgeToEdge
            ? 0
            : (fullscreen ? 0 : headerBounds.height),
          height: edgeToEdge ? "100%" : gridBounds.height,
          "--call-view-safe-area-inset-top":
            edgeToEdge && headerStyle !== HeaderStyle.None && showHeader
              ? headerStyle === HeaderStyle.AppBar
                ? `${bounds.top}px`
                : `calc(env(safe-area-inset-top) + ${headerBounds.height}px)`
              : undefined,
          "--call-view-safe-area-inset-bottom":
            edgeToEdge && showFooter && !overlayControls
              ? `calc(env(safe-area-inset-bottom) + ${footerBounds.height}px)`
              : undefined,
        }}
        model={layout}
        Layout={layers.fixed}
        Tile={Tile}
        aria-hidden={contentObscured}
      />
    );
    const scrollingGrid = (
      <Grid
        key="scrolling"
        className={classNames(styles.scrollingGrid, {
          [styles.foregroundGrid]: layers.foreground === "scrolling",
        })}
        model={layout}
        Layout={layers.scrolling}
        Tile={Tile}
        aria-hidden={contentObscured}
      />
    );

    switch (layers.foreground) {
      case "fixed":
        return (
          <>
            {scrollingGrid}
            {fixedGrid}
          </>
        );
      case "scrolling":
        return (
          <>
            {fixedGrid}
            {scrollingGrid}
          </>
        );
    }
  };

  const rageshakeRequestModalProps = useRageshakeRequestModal(
    matrixRoom.roomId,
  );

  useAppBarSecondaryButton(
    <SettingsIconButton
      key="settings"
      onClick={() => setSettingsOpen(true)}
      data-testid="settings-app-bar"
    />,
  );

  const [chatToggleTarget, setChatToggleTarget] =
    useState<HTMLDivElement | null>(null);
  const footer = footerVm !== null && (
    <FullscreenCallControls active={overlayControls}>
      <CallFooter
        className={styles.footer}
        ref={footerRef}
        vm={footerVm}
        chatToggleRef={setChatToggleTarget}
      />
    </FullscreenCallControls>
  );

  return (
    <div
      className={classNames(styles.inRoom, {
        [styles.overflowing]: overflowing,
      })}
      ref={containerRef}
      data-fullscreen={fullscreen}
      data-screen-share={spotlightHasScreenShare}
      onPointerDown={onViewPointerDown}
      onPointerCancel={() => {
        touchStart.current = null;
      }}
      onPointerUp={onViewPointerUp}
      onPointerMove={onPointerMove}
      onPointerOut={onPointerOut}
    >
      {!fullscreen && header}
      {fullscreen && (
        <button
          className={styles.exitFullscreen}
          aria-label={t("instance.ui.exit_fullscreen")}
          onClick={() => void exitCallFullscreen()}
        >
          <FullScreenMinimiseIcon aria-hidden width={20} height={20} />
        </button>
      )}
      {audioParticipants.map(({ livekitRoom, url, participants }) => (
        <LivekitRoomAudioRenderer
          key={url}
          url={url}
          livekitRoom={livekitRoom}
          validIdentities={participants}
          muted={muteAllAudio}
        />
      ))}

      {renderContent()}
      {AI_ENABLED && showTranscriptPanel && (
        <div
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: "360px",
            backgroundColor: "rgba(20, 20, 22, 0.85)",
            borderLeft: "1px solid rgba(255, 255, 255, 0.08)",
            display: "flex",
            flexDirection: "column",
            zIndex: 900,
            boxShadow: "-8px 0 32px rgba(0,0,0,0.4)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
          }}
        >
          <div
            style={{
              padding: "20px",
              borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <h3
                style={{
                  margin: 0,
                  color: "#fff",
                  fontSize: "16px",
                  fontWeight: 500,
                  letterSpacing: "0.3px",
                }}
              >
                {uiText.liveTranscript}
              </h3>
              {isTranscriptionActive && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    background: "rgba(52, 199, 89, 0.15)",
                    color: "#34c759",
                    border: "1px solid rgba(52, 199, 89, 0.3)",
                    padding: "4px 8px",
                    borderRadius: "12px",
                    fontSize: "11px",
                    fontWeight: 600,
                  }}
                >
                  <div
                    style={{
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      backgroundColor: "currentColor",
                      animation: "pulse 2s infinite",
                    }}
                  />
                  REC
                </div>
              )}
            </div>
            <button
              onClick={() => setShowTranscriptPanel(false)}
              style={{
                background: "transparent",
                border: "none",
                color: "#666",
                cursor: "pointer",
                padding: "4px",
                transition: "color 0.2s",
              }}
              onMouseOver={(e) => (e.currentTarget.style.color = "#fff")}
              onMouseOut={(e) => (e.currentTarget.style.color = "#666")}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div
            ref={transcriptScrollRef}
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "20px 16px",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              scrollBehavior: "smooth",
            }}
          >
            <div style={{ flex: 1 }}></div>
            {transcripts.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  color: "#555",
                  paddingBottom: "20px",
                  fontSize: "14px",
                }}
              >
                {isTranscriptionActive
                  ? uiText.listeningParticipants
                  : uiText.transcriptEmptyHint}
              </div>
            )}
            {transcripts.map((msg, index) => {
              const isSameAsPrev =
                index > 0 &&
                transcripts[index - 1].participant === msg.participant;
              const isSameAsNext =
                index < transcripts.length - 1 &&
                transcripts[index + 1].participant === msg.participant;
              return (
                <div
                  key={msg.id}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    marginTop: isSameAsPrev ? "2px" : "24px",
                    width: "100%",
                  }}
                >
                  {!isSameAsPrev && (
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#888",
                        marginBottom: "8px",
                        marginLeft: "16px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontWeight: 600,
                      }}
                    >
                      {getDisplayName(msg.participant)}
                    </span>
                  )}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      maxWidth: "92%",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: msg.isFinal
                          ? "rgba(255, 255, 255, 0.08)"
                          : "transparent",
                        border: msg.isFinal
                          ? "1px solid rgba(255, 255, 255, 0.03)"
                          : "1px dashed rgba(255, 255, 255, 0.2)",
                        color: msg.isFinal ? "#f0f0f0" : "#a0a0a0",
                        padding: "12px 18px",
                        borderRadius: "20px",
                        borderTopLeftRadius: isSameAsPrev ? "6px" : "20px",
                        borderBottomLeftRadius: isSameAsNext ? "6px" : "20px",
                        fontSize: "15px",
                        lineHeight: "1.5",
                        transition: "all 0.3s ease-in-out",
                        position: "relative",
                      }}
                    >
                      {msg.text}
                      {!msg.isFinal && (
                        <span style={{ opacity: 0.5, marginLeft: "4px" }}>
                          ...
                        </span>
                      )}
                    </div>
                    {msg.isFinal && msg.timestamp && (
                      <button
                        onClick={() => toggleStar(msg.timestamp)}
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          padding: "4px",
                          fontSize: "18px",
                          opacity: starredTimestamps.has(msg.timestamp)
                            ? 1
                            : 0.3,
                          filter: starredTimestamps.has(msg.timestamp)
                            ? "drop-shadow(0 0 8px rgba(255, 215, 0, 0.6))"
                            : "none",
                          transition: "all 0.2s",
                          transform: starredTimestamps.has(msg.timestamp)
                            ? "scale(1.1)"
                            : "scale(1)",
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.opacity = "1")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.opacity =
                            starredTimestamps.has(msg.timestamp) ? "1" : "0.3")
                        }
                        title={uiText.markImportant}
                        aria-label={uiText.markImportant}
                      >
                        {starredTimestamps.has(msg.timestamp) ? "🌟" : "⭐"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <CallEventAudioRenderer vm={vm} muted={muteAllAudio} />
      <ReactionsAudioRenderer vm={vm} muted={muteAllAudio} />
      <RingingAudioRenderer vm={ringingVm} muted={muteAllAudio} />
      {pipError && (
        <Toast open onDismiss={dismissPipError}>
          {pipError}
        </Toast>
      )}
      {reconnectingToast}
      {earpieceOverlay}
      <ReactionsOverlay vm={vm} />

      <video
        ref={pipVideoRef}
        muted
        playsInline
        autoPlay
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          width: "2px",
          height: "2px",
          opacity: 0,
          pointerEvents: "none",
          zIndex: -1,
        }}
      />

      {livekitRoom && (
        <RoomContext.Provider value={livekitRoom}>
          <ChatWidget
            displayName={matrixInfo.displayName}
            toggleTarget={chatToggleTarget}
          />
        </RoomContext.Provider>
      )}

      {footer}
      {layout.type !== "pip" && (
        <>
          <RageshakeRequestModal {...rageshakeRequestModalProps} />
          <SettingsModal
            client={client}
            roomId={matrixRoom.roomId}
            open={settingsOpen}
            onDismiss={(): void => setSettingsOpen(false)}
            tab={settingsTab}
            onTabChange={setSettingsTab}
            livekitRooms={allConnections
              .getConnections()
              .map((connectionItem) => ({
                room: connectionItem.livekitRoom,
                livekitAlias: connectionItem.livekitAlias,
                isLocal: undefined,
                url: connectionItem.transport.livekit_service_url,
              }))}
          />
        </>
      )}
    </div>
  );
};
